'use client';

import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getProductUrl, type ProductHit } from '@libs/algolia';
import { AccountDrawerType } from '@libs/common-next';
import type {
    AddDonationRequestBody,
    AddItemRequestBody,
    ApplyPromoCodeRequestBody,
    BaseError,
    CartBadRequestErrorResponse,
    CartCreateBody,
    CartUI,
    ErrorResponseBody,
    GuestEmailRequestBody,
    InvalidFieldError,
    LineItemResponseError,
    PickupInfoRequestBody,
    PlaceOrderRequestBody,
    RemoveItemRequestBody,
    ShippingMethods,
    TreatPointRequestBody,
    UpdateItemFulfillmentMethodRequestBody,
    UpdateItemQtyRequestBody,
    UpdateItemStoreRequestBody,
} from '@libs/common-node';
import { type CartNotification } from '@libs/common-types';
import { COOKIES, DeliveryType, getAccessToken, HttpStatus, NAVIGATION_URLS } from '@libs/core';

import { CART_ERROR_MESSAGES, COMPONENTS, INVALID_FIELD_ERROR, SENTRY_CONTEXT } from '../../constants';
import { useCartNotifications, usePageLayout } from '../../hooks';
import type {
    CartHandleValidationOptions,
    CartValidationErrorDetails,
    IdsList,
    ItemsListItem,
    RefetchCartStateOptions,
    SetUpdatingOptions,
    StockErrorItem,
} from '../../types';
import { PageType } from '../../types';
import {
    addDiscountCode as addDiscountCodeApi,
    excludeDiscontinuedValidationErrors,
    getAutoshipPromo,
    getCartById,
    getENV,
    logConsoleError,
    logSentryError,
    mergeCartNotificationsPreservingDiscontinuedRemoved,
    mergeCartNotificationsPreservingValidateOnLoad,
    removeLineItemsFromCart,
    runDiscontinuedItemsRemovedRecovery,
    updateSubscriptionStatus,
    validateCart,
} from '../../utils';
import {
    buildCartProductSkuKeyMap,
    fetchCartProductsBySkus,
    lineItemSkuToProductMapKey,
} from '../../utils/cartProductsByIds';
import {
    checkHasBannerOverridingSchedule,
    filterScheduledDeliveryNotificationsForValidationErrors,
    getNotificationsFromCartValidationResponse,
    hasValidationErrorCode,
    isScheduledDeliveryDateUpdatedNotification,
    mergeValidationNotificationsForSingleUpdateCycle,
} from '../../utils/cartValidationNotificationUtils';
import {
    createClientIneligibleDiscountCode,
    getClientIneligibleCodesFromStorage,
    getCodeFromClientIneligibleDiscountCodeId,
    isClientIneligibleDiscountCode,
    isClientIneligibleDiscountCodeId,
    restoreClientIneligibleCodes,
    setClientIneligibleCodesInStorage,
} from '../../utils/clientIneligibleDiscountCodes';
import {
    CartValidationErrorKey,
    cartValidationNotificationMap,
    checkoutValidationNotificationMap,
} from '../../utils/notificationMaps';
import {
    consumePendingScheduledDeliveryDateChange,
    replaceWithLineItemAgnosticScheduledDeliveryDateUpdatedNotification,
} from '../../utils/scheduledDeliveryValidationRecovery';
import { asyncForEach } from './asyncForEach';
import { isCartIdentityStale } from './cartIdentity';
import { CartUpdateError, CartValidationError, CheckoutPaymentError, getShouldIgnoreError } from './error';
import { getShouldIgnorePaymentError } from './error/cartPaymentIgnoreErrors';
import { parseStockErrors } from './error/parseStockErrors';
import fetchAndMergeStores from './fetchAndMergeStores';
import { parseCheckoutPaymentErrorResponse, parseErrorResponse, unexpectedError } from './parseErrorResponse';

/**
 * Parses cart/checkout validation errors into details without React state updates.
 * Shared by `handleCartError` and `handleCartValidation` so parsing rules stay in one place.
 */
function parseCartFlowErrorToValidationDetails(error: unknown): CartValidationErrorDetails[] {
    if (error instanceof CheckoutPaymentError) {
        return parseCheckoutPaymentErrorResponse(error.errorDetails);
    }
    if (error instanceof CartUpdateError) {
        return parseErrorResponse(error.errorDetails, error.attemptedFulfillmentMethod);
    }
    if (error instanceof CartValidationError) {
        return parseErrorResponse(error.errorDetails);
    }
    if (error instanceof Error) {
        return [unexpectedError];
    }
    return [];
}

const CART_PRODUCTS_BY_IDS_CALLER = 'web:cart:useCartState-client';

type RequestBodyType =
    | { cartId: string }
    | CartCreateBody
    | AddItemRequestBody
    | RemoveItemRequestBody
    | UpdateItemQtyRequestBody
    | AddDonationRequestBody
    | GuestEmailRequestBody
    | PickupInfoRequestBody
    | ApplyPromoCodeRequestBody
    | PlaceOrderRequestBody
    | TreatPointRequestBody;

type CartUpdater<T = RequestBodyType> = (params: {
    itemId: string;
    cartId: string;
    version: number;
    body: T;
}) => Promise<CartUI | null>;

type CartUpdateOptions = {
    validateCartOnError?: boolean;
    skipCommit?: boolean;
    preserveUpdatingState?: boolean;
    version?: number;
    cartId?: string;
};

const FALLBACK_PRODUCT_URL = '#'; // Will be placed if cart has corrupted product, but TODO: it should be sorted out
export const skuRedirectUrl = (sku?: string) => (sku ? `/${encodeURIComponent(sku)}.html` : FALLBACK_PRODUCT_URL);

/**
 * Determines if an HTTP response status code represents an error. Relies on the status being there.
 * Errors are considered in ranges:
 * - 4xx: Client errors
 * - 5xx: Server errors
 *
 * @param status - The HTTP status code to check.
 * @returns True if the status code represents an error; otherwise, false.
 */

const isErrorStatus = (status: number): boolean => {
    return status >= 400 && status < 600;
};

const FF_METHOD_CANNOT_BE_CHANGED_ERR = 'cart.fulfillmentMethod.cannotBeChanged';

type FulfillmentMethodChangeLineItemError = {
    errorCode?: string;
};

type FulfillmentMethodChangeLineError = {
    lineItemErrors?: FulfillmentMethodChangeLineItemError[];
};

type FulfillmentMethodChangeErrorBody = {
    errors?: {
        lineErrors?: FulfillmentMethodChangeLineError[];
    };
};

const getFulfillmentMethodErrorDedupeKey = ({
    errorCode,
    lineItemId,
    productName,
}: Pick<CartValidationErrorDetails, 'errorCode' | 'lineItemId' | 'productName'>): string =>
    [errorCode, lineItemId ?? '', productName ?? ''].join('|');

async function refetchAndPatchCartAfterScheduleClearedOnValidateFailure(
    parsedValidationErrors: CartValidationErrorDetails[],
    validationNotifications: CartNotification[],
    cartId: string,
    refreshCartState: (options?: RefetchCartStateOptions) => Promise<CartUI | null>,
    setCart: (next: CartUI) => void,
    applyCartDerivedLists: (next: CartUI) => void
): Promise<void> {
    const hasInvalidScheduledDateError = hasValidationErrorCode(
        parsedValidationErrors,
        CartValidationErrorKey.AUTOSHIP_SCHEDULED_DATE_INVALID_FULFILLMENT_METHOD
    );
    const hasScheduledDateUpdatedNotification = validationNotifications.some(
        isScheduledDeliveryDateUpdatedNotification
    );

    const scheduleWasClearedOnServer =
        !checkHasBannerOverridingSchedule(parsedValidationErrors) &&
        (hasInvalidScheduledDateError || hasScheduledDateUpdatedNotification);

    if (!scheduleWasClearedOnServer || !cartId) {
        return;
    }

    try {
        const refreshedCart = await refreshCartState({
            preserveUpdatingState: true,
            mergePersistedNotifications: true,
        });
        if (!refreshedCart) {
            return;
        }
        setCart(refreshedCart);
        applyCartDerivedLists(refreshedCart);
    } catch (refreshError) {
        logConsoleError(JSON.stringify(refreshError), {
            component: COMPONENTS.CART_PROVIDER,
            severity: 'HIGH',
            additionalInfo: `${CART_ERROR_MESSAGES.CART_UPDATE_FAILED} | Cart refetch failed after scheduled delivery invalidation`,
        });
    }
}

const getFulfillmentMethodErrors = (parsedErrors: CartValidationErrorDetails[]): CartValidationErrorDetails[] => {
    return parsedErrors.filter(
        parsedError => parsedError.errorCode === CartValidationErrorKey.FULFILLMENT_METHOD_CANNOT_BE_CHANGED
    );
};

const getCartItemsByIdWithFulfillmentMethod = (cart: CartUI) =>
    new Map(
        cart.fulfillmentGroups.flatMap(group =>
            group.items.map(item => [item.id, { item, fulfillmentMethod: group.fulfillmentMethod }] as const)
        )
    );

const getPreservedFulfillmentMethodErrors = (
    currentErrors: CartValidationErrorDetails[],
    updatedCart: CartUI,
    previousCart?: CartUI
): CartValidationErrorDetails[] => {
    const updatedCartItemsById = getCartItemsByIdWithFulfillmentMethod(updatedCart);
    const previousCartItemsById = previousCart ? getCartItemsByIdWithFulfillmentMethod(previousCart) : null;
    const hasScheduledAutoshipItem = Array.from(updatedCartItemsById.values()).some(
        ({ item }) =>
            item.autoshipFrequency != null &&
            item.autoshipFrequencyUnitOfMeasure != null &&
            item.scheduledDeliveryDate != null
    );

    return getFulfillmentMethodErrors(currentErrors).filter(error => {
        if (!error.lineItemId) {
            return false;
        }

        const matchingUpdatedItem = updatedCartItemsById.get(error.lineItemId);

        if (!matchingUpdatedItem) {
            return false;
        }

        const previousMatchingItem = previousCartItemsById?.get(error.lineItemId);
        const hasChangedFulfillmentMethod =
            previousMatchingItem !== undefined &&
            previousMatchingItem.fulfillmentMethod !== matchingUpdatedItem.fulfillmentMethod;

        if (hasChangedFulfillmentMethod) {
            return false;
        }

        const hasAutoshipSelection =
            matchingUpdatedItem.item.autoshipFrequency != null &&
            matchingUpdatedItem.item.autoshipFrequencyUnitOfMeasure != null;

        return (
            hasScheduledAutoshipItem && hasAutoshipSelection && matchingUpdatedItem.item.scheduledDeliveryDate == null
        );
    });
};

const getMixedCartOverwriteFulfillmentMethodErrors = (
    currentErrors: CartValidationErrorDetails[],
    updatedCart: CartUI
): CartValidationErrorDetails[] => {
    const updatedCartItemsById = getCartItemsByIdWithFulfillmentMethod(updatedCart);

    return getFulfillmentMethodErrors(currentErrors).filter(error => {
        if (!error.lineItemId) {
            return false;
        }

        const matchingUpdatedItem = updatedCartItemsById.get(error.lineItemId);

        if (!matchingUpdatedItem) {
            return false;
        }

        const hasAutoshipSelection =
            matchingUpdatedItem.item.autoshipFrequency != null &&
            matchingUpdatedItem.item.autoshipFrequencyUnitOfMeasure != null;

        return hasAutoshipSelection && matchingUpdatedItem.fulfillmentMethod !== DeliveryType.STH;
    });
};

const mergePreservedValidationErrors = (
    preservedErrors: CartValidationErrorDetails[],
    parsedValidationErrors: CartValidationErrorDetails[]
): CartValidationErrorDetails[] => {
    const seenFulfillmentMethodErrors = new Set<string>();

    return [...preservedErrors, ...parsedValidationErrors].filter(error => {
        if (error.errorCode !== CartValidationErrorKey.FULFILLMENT_METHOD_CANNOT_BE_CHANGED) {
            return true;
        }

        const dedupeKey = getFulfillmentMethodErrorDedupeKey(error);

        if (seenFulfillmentMethodErrors.has(dedupeKey)) {
            return false;
        }

        seenFulfillmentMethodErrors.add(dedupeKey);

        return true;
    });
};

const groupFulfillmentMethodErrorsByLineItem = (
    fulfillmentMethodErrors: CartValidationErrorDetails[]
): LineItemResponseError[] => {
    const lineErrorsByKey = new Map<string, LineItemResponseError>();

    fulfillmentMethodErrors.forEach(error => {
        if (!error.lineItemId) {
            return;
        }

        const lineErrorKey = [error.lineItemId, error.productName ?? ''].join('|');
        const existingLineError = lineErrorsByKey.get(lineErrorKey);
        const lineItemError = {
            errorCode: error.errorCode,
            message: error.message,
            ...(error.available === undefined ? {} : { available: error.available }),
        };

        if (existingLineError) {
            existingLineError.lineItemErrors.push(lineItemError);
            return;
        }

        lineErrorsByKey.set(lineErrorKey, {
            lineItemId: error.lineItemId,
            ...(error.productName ? { productName: error.productName } : {}),
            lineItemErrors: [lineItemError],
        });
    });

    return Array.from(lineErrorsByKey.values());
};

export const mergeUnresolvedFulfillmentMethodErrorsIntoCartValidationError = (
    validationError: CartValidationError,
    currentErrors: CartValidationErrorDetails[],
    updatedCart: CartUI
): CartValidationError => {
    const parsedValidationErrors = parseErrorResponse(validationError.errorDetails);
    const preservedFulfillmentMethodErrors = getPreservedFulfillmentMethodErrors(currentErrors, updatedCart);
    const mixedCartOverwriteFulfillmentMethodErrors = parsedValidationErrors.some(
        error => error.errorCode === CartValidationErrorKey.MIXED_CART_BANNER
    )
        ? getMixedCartOverwriteFulfillmentMethodErrors(currentErrors, updatedCart)
        : [];
    const mergedPreservedFulfillmentMethodErrors = [
        ...preservedFulfillmentMethodErrors,
        ...mixedCartOverwriteFulfillmentMethodErrors.filter(error => !preservedFulfillmentMethodErrors.includes(error)),
    ];

    if (!mergedPreservedFulfillmentMethodErrors.length) {
        return validationError;
    }

    const existingFulfillmentMethodErrors = getFulfillmentMethodErrors(parsedValidationErrors);
    const existingFulfillmentMethodErrorKeys = new Set(
        existingFulfillmentMethodErrors.map(getFulfillmentMethodErrorDedupeKey)
    );

    const additionalFulfillmentMethodErrors = mergedPreservedFulfillmentMethodErrors.filter(
        error => !existingFulfillmentMethodErrorKeys.has(getFulfillmentMethodErrorDedupeKey(error))
    );

    if (!additionalFulfillmentMethodErrors.length) {
        return validationError;
    }

    return new CartValidationError(validationError.message, {
        ...validationError.errorDetails,
        errors: {
            ...validationError.errorDetails.errors,
            lineErrors: [
                ...(validationError.errorDetails.errors?.lineErrors ?? []),
                ...groupFulfillmentMethodErrorsByLineItem(additionalFulfillmentMethodErrors),
            ],
        },
    });
};

const hasFulfillmentMethodCannotBeChangedError = (updatedCart: CartUI | null, body: unknown): boolean => {
    const attemptedFulfillmentMethod = (body as UpdateItemFulfillmentMethodRequestBody | undefined)?.fulfillmentMethod;

    if (!attemptedFulfillmentMethod) {
        return false;
    }

    const updateResponse = updatedCart as (CartUI & FulfillmentMethodChangeErrorBody) | null;

    return (
        updateResponse?.errors?.lineErrors?.some((lineError: FulfillmentMethodChangeLineError) =>
            lineError.lineItemErrors?.some(
                (lineItemError: FulfillmentMethodChangeLineItemError) =>
                    lineItemError.errorCode === FF_METHOD_CANNOT_BE_CHANGED_ERR
            )
        ) ?? false
    );
};

const pageTypesRequiringStoreInfo = [PageType.CART, PageType.CHECKOUT];

const getSortedUniqueSkus = (cart: CartUI): string[] => {
    const skus = cart.fulfillmentGroups?.flatMap(group => group.items.map(item => item.sku)) ?? [];
    return [...new Set(skus)].sort((a, b) => a.localeCompare(b));
};

const areCartSkuSetsEqual = (cartA: CartUI, cartB: CartUI): boolean =>
    getSortedUniqueSkus(cartA).join('\0') === getSortedUniqueSkus(cartB).join('\0');

/**
 * Preserves enriched data from previous cart state to new cart state.
 * Copies productUrl, autoshipDiscount, webStyleId, and store
 * from previous cart items to new cart items by matching item IDs.
 *
 * @param newCart - The new cart state from API
 * @param previousCart - The previous cart state with enriched data
 * @param preserveStoreData - Whether to preserve store data (default: true). Set to false when store needs to be refreshed.
 * @returns The new cart with preserved enriched data
 */
const preserveEnrichedData = (newCart: CartUI, previousCart: CartUI, preserveStoreData = true): CartUI => {
    // Create a map of previous items by ID for quick lookup
    const previousItemsMap = new Map<
        string,
        { item: (typeof previousCart.fulfillmentGroups)[0]['items'][0]; groupIndex: number; itemIndex: number }
    >();

    previousCart.fulfillmentGroups.forEach((group, groupIndex) => {
        group.items.forEach((item, itemIndex) => {
            previousItemsMap.set(item.id, { item, groupIndex, itemIndex });
        });
    });

    // Create a map of previous fulfillment groups by storeId for store data preservation
    const previousGroupsMap = new Map<string | number | null, (typeof previousCart.fulfillmentGroups)[0]>();
    previousCart.fulfillmentGroups.forEach(group => {
        if (group.storeId) {
            previousGroupsMap.set(group.storeId, group);
        }
    });

    const enrichedCart = structuredClone(newCart);

    // Preserve enriched data for items
    enrichedCart.fulfillmentGroups.forEach(group => {
        group.items.forEach(item => {
            const previousItemData = previousItemsMap.get(item.id);

            if (previousItemData) {
                // Preserve product enrichment fields
                item.productUrl = previousItemData.item.productUrl;
                item.autoshipDiscount = previousItemData.item.autoshipDiscount;
                item.webStyleId = previousItemData.item.webStyleId;
            }

            // Defensive fallback: some callers (e.g. CartProvider.addItem) may set raw API carts into state.
            // Ensure critical enrichment fields are never left undefined.
            if (!item.productUrl) {
                item.productUrl = skuRedirectUrl(item.sku);
            }
            if (typeof item.autoshipDiscount === 'undefined') {
                item.autoshipDiscount = null;
            }
        });

        // Preserve store data if requested and storeId matches
        if (preserveStoreData && group.storeId) {
            const previousGroup = previousGroupsMap.get(group.storeId);
            if (previousGroup?.store) {
                group.store = previousGroup.store;
            }
        }
    });

    return enrichedCart;
};

const useCartState = (cartId: string) => {
    const queryClient = useQueryClient();
    const { pageType, openAccountDrawer } = usePageLayout();
    const isLoggedIn = getAccessToken();
    const autoshipDiscountUpdatesEnabled = getENV('NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED');

    const EMPTY_CART = null;

    const init = useRef(false);
    const pathname = usePathname();

    // the cartId this tab last synced against, whether or not it resolved. Compared with the shared
    // cookie to detect carts changed by another tab.
    const lastSyncedCartIdRef = useRef<string | undefined>(cartId || undefined);

    // set when a reconcile is skipped because a cart write is in flight, so it can run once that
    // write settles instead of waiting for the next focus.
    const hasPendingReconcileRef = useRef(false);

    // mirrors the committed cart so an in-flight refetch can tell whether another write replaced it
    // meanwhile. Tracks the whole cart rather than its id, so a new version of the same cart counts.
    const latestCartRef = useRef<CartUI | null>(EMPTY_CART);

    const [cart, setCart] = useState<CartUI | null>(EMPTY_CART);
    const [idsList, setIdsList] = useState<IdsList>([]);
    const [cartShippingOptionsMessage, setCartShippingOptionsMessage] = useState('');
    const [itemsList, setItemsList] = useState<ItemsListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const updatingInFlightRef = useRef(0);
    // Paired setUpdating(true)/false from addItem, Afterpay, etc. Must not share storage with
    // preserveUpdatingState: an addItem finally { setUpdating(false) } or a PayPal cancel
    // unpaired false would otherwise drop a skipCommit hold, let isUpdating fall false, and
    // run a deferred identity reconcile that can overwrite a fresher cart with a stale merge snapshot.
    const manualUpdatingHoldCountRef = useRef(0);
    const hasPreserveUpdatingHoldRef = useRef(false);

    const syncIsUpdating = () => {
        const nextIsUpdating =
            updatingInFlightRef.current > 0 ||
            manualUpdatingHoldCountRef.current > 0 ||
            hasPreserveUpdatingHoldRef.current;
        setIsUpdating(current => (current === nextIsUpdating ? current : nextIsUpdating));
    };

    const beginUpdating = () => {
        updatingInFlightRef.current += 1;
        syncIsUpdating();
    };

    const endUpdating = () => {
        updatingInFlightRef.current = Math.max(0, updatingInFlightRef.current - 1);
        syncIsUpdating();
    };

    const clearPreserveUpdatingHold = () => {
        hasPreserveUpdatingHoldRef.current = false;
    };

    const isPreserveUpdatingHoldBlocking = (ignorePreserveUpdatingHold = false) =>
        hasPreserveUpdatingHoldRef.current && !ignorePreserveUpdatingHold;

    const restorePreviouslySyncedCartId = (previouslySyncedCartId: string | undefined) => {
        lastSyncedCartIdRef.current = previouslySyncedCartId;
        return latestCartRef.current;
    };

    const shouldAbandonUnpublishedRefetch = (
        discardIfCartChanged: boolean,
        cartBeforeRefetch: CartUI | null,
        ignorePreserveUpdatingHold: boolean
    ) =>
        (discardIfCartChanged && latestCartRef.current !== cartBeforeRefetch) ||
        isPreserveUpdatingHoldBlocking(ignorePreserveUpdatingHold);

    const setUpdating = (updating: boolean, options?: SetUpdatingOptions) => {
        if (updating) {
            manualUpdatingHoldCountRef.current += 1;
        } else if (options?.releasePreserveUpdatingHold) {
            clearPreserveUpdatingHold();
        } else if (manualUpdatingHoldCountRef.current > 0) {
            manualUpdatingHoldCountRef.current -= 1;
        }
        syncIsUpdating();
    };
    const [errors, setErrors] = useState<CartValidationErrorDetails[]>([]);
    const [stockErrors, setStockErrors] = useState<StockErrorItem[]>([]);
    const { setNotifications } = useCartNotifications();
    const [isCartNotFound, setIsCartNotFound] = useState<boolean>();
    const [shippingMethodsList, setShippingMethodsList] = useState<ShippingMethods[]>([]);
    // Set true when a checkout-page validation removes discontinued items, signalling the cart-app
    // consumer to navigate back to /cart. Navigation is left to the consumer (which owns the App
    // Router) so this shared hook stays free of useRouter — see CheckoutRedirectOnDiscontinued.
    const [shouldRedirectToCart, setShouldRedirectToCart] = useState(false);
    const prepareItemsForParsing = (cartToParse: CartUI): ItemsListItem[] => {
        const itemsForParsing: ItemsListItem[] = [];

        cartToParse.fulfillmentGroups?.forEach(group => {
            group.items.forEach(item => {
                itemsForParsing.push({
                    ...item,
                    storeId: `${group.storeId}`,
                    fulfillmentMethod: group.fulfillmentMethod,
                });
            });
        });

        return itemsForParsing;
    };

    // a null cart empties both lists, so a cart dropped for any reason cannot leave consumers deriving
    // line items or banners from a cart that is gone.
    const applyCartDerivedLists = useCallback((nextCart: CartUI | null) => {
        const ids: IdsList = [];
        const items: Array<ItemsListItem> = [];
        nextCart?.fulfillmentGroups?.forEach(group => {
            group.items.forEach(item => {
                ids.push(item.id);
                items.push({
                    ...item,
                    storeId: `${group.storeId}`,
                    fulfillmentMethod: group.fulfillmentMethod,
                });
            });
        });
        setIdsList(ids);
        setItemsList(items);
    }, []);

    const removeLineItemsFromCartState = useCallback(
        (lineItemIds: string[]) => {
            setCart(prev => {
                if (!prev) {
                    return prev;
                }
                const next = removeLineItemsFromCart(prev, lineItemIds);
                applyCartDerivedLists(next);
                return next;
            });
        },
        [applyCartDerivedLists]
    );

    const createProductMetadata = useCallback(
        (sku: string, existingProductUrl: string | undefined, productMap?: Map<number | undefined, ProductHit>) => {
            const mapKey = lineItemSkuToProductMapKey(sku);
            const product = mapKey == null ? undefined : productMap?.get(mapKey);
            const normalizedExistingProductUrl = existingProductUrl?.trim();
            const safeExistingProductUrl =
                normalizedExistingProductUrl && normalizedExistingProductUrl !== FALLBACK_PRODUCT_URL
                    ? normalizedExistingProductUrl
                    : undefined;
            // Use variant SKU in the path so PDP opens the same flavor/variant as the line item (not master default).
            const productUrl = product ? getProductUrl(product, true) : (safeExistingProductUrl ?? skuRedirectUrl(sku));
            const masterProductID = product?.masterProductID;
            const webStyleId = masterProductID == null ? undefined : String(masterProductID);

            return {
                productUrl,
                autoshipDiscount: product ? getAutoshipPromo(product.eligiblePromotions)?.discountDisplay : null,
                product,
                webStyleId,
            };
        },
        []
    );

    const getCartProductsByIds = async (cart: CartUI): Promise<Map<number | undefined, ProductHit>> => {
        const skus = cart.fulfillmentGroups.flatMap(group => group.items.map(item => item.sku));

        // Return empty map if there are no items to fetch (empty cart) to prevent unnecessary cart errors
        if (skus.length === 0) {
            return new Map<number | undefined, ProductHit>();
        }

        const products: ProductHit[] = await fetchCartProductsBySkus(
            queryClient,
            skus,
            CART_PRODUCTS_BY_IDS_CALLER
        ).catch(() => []);
        return buildCartProductSkuKeyMap(products);
    };

    const enrichCart = useCallback(
        async (
            cartRes: CartUI,
            previousCart?: CartUI | null,
            options?: { refreshStoreData?: boolean; prefetchedProductMap?: Map<number | undefined, ProductHit> }
        ): Promise<CartUI> => {
            let productMap: Map<number | undefined, ProductHit> | undefined;

            // If previous cart exists, preserve enriched data and skip product lookup fetch
            let enrichedCart: CartUI;
            const shouldPreserveData = previousCart && previousCart.id === cartRes.id;
            const shouldRefreshStoreData = options?.refreshStoreData ?? false;

            if (shouldPreserveData) {
                // Preserve enriched data from previous cart
                // Only preserve store data if we're not refreshing it
                enrichedCart = preserveEnrichedData(cartRes, previousCart, !shouldRefreshStoreData);

                // Identify new items that weren't in the previous cart and need enrichment
                const previousItemIds = new Set<string>();
                previousCart.fulfillmentGroups.forEach(group => {
                    group.items.forEach(item => {
                        previousItemIds.add(item.id);
                    });
                });

                const newItemSkus: string[] = [];
                enrichedCart.fulfillmentGroups.forEach(group => {
                    group.items.forEach(item => {
                        if (!previousItemIds.has(item.id)) {
                            // This is a new item that needs enrichment
                            newItemSkus.push(item.sku);
                        }
                    });
                });

                // If there are new items, fetch their product data and enrich them
                // Note: productUrl and autoshipDiscount are always needed, not just for store pages
                if (newItemSkus.length > 0 && pageTypesRequiringStoreInfo.includes(pageType)) {
                    const newProducts = await fetchCartProductsBySkus(
                        queryClient,
                        newItemSkus,
                        CART_PRODUCTS_BY_IDS_CALLER
                    );
                    const newProductMap = buildCartProductSkuKeyMap(newProducts);

                    // Enrich new items with productUrl, autoshipDiscount, and webStyleId (Algolia masterProductID)
                    enrichedCart.fulfillmentGroups.forEach(group => {
                        group.items.forEach(item => {
                            if (!previousItemIds.has(item.id)) {
                                const { productUrl, autoshipDiscount, webStyleId } = createProductMetadata(
                                    item.sku,
                                    item.productUrl,
                                    newProductMap
                                );

                                item.productUrl = productUrl;
                                item.autoshipDiscount = autoshipDiscount;
                                if (webStyleId != null) {
                                    item.webStyleId = webStyleId;
                                }
                            }
                        });
                    });
                }
            } else {
                // Full enrichment on initial load or when cart ID changes
                enrichedCart = structuredClone(cartRes);

                if (pageTypesRequiringStoreInfo.includes(pageType)) {
                    productMap = options?.prefetchedProductMap ?? (await getCartProductsByIds(cartRes));
                }

                await asyncForEach(enrichedCart.fulfillmentGroups, async group => {
                    await asyncForEach(group.items, async item => {
                        const { productUrl, autoshipDiscount, webStyleId } = createProductMetadata(
                            item.sku,
                            item.productUrl,
                            productMap
                        );

                        item.productUrl = productUrl;
                        item.autoshipDiscount = autoshipDiscount;
                        if (webStyleId != null) {
                            item.webStyleId = webStyleId;
                        }
                    });
                });

                if (pageTypesRequiringStoreInfo.includes(pageType)) {
                    enrichedCart = await fetchAndMergeStores(enrichedCart, queryClient);
                }
            }

            // If we preserved data but need fresh store data, fetch stores only
            if (shouldPreserveData && shouldRefreshStoreData && pageTypesRequiringStoreInfo.includes(pageType)) {
                enrichedCart = await fetchAndMergeStores(enrichedCart, queryClient);
            }

            const restoredCart = restoreClientIneligibleCodes(enrichedCart, previousCart);
            applyCartDerivedLists(restoredCart);
            return restoredCart;
        },
        [applyCartDerivedLists, createProductMetadata, pageType, queryClient]
    );

    const logParsedErrors = (error: unknown, parsedErrors: CartValidationErrorDetails[]) => {
        if (parsedErrors.length > 0) {
            for (const parsedError of parsedErrors) {
                const errorCode = parsedError.errorCode;

                const notification =
                    cartValidationNotificationMap[errorCode as keyof typeof cartValidationNotificationMap] ||
                    checkoutValidationNotificationMap[errorCode as keyof typeof checkoutValidationNotificationMap];

                if (notification) {
                    logConsoleError(
                        `Error code: <${errorCode}> | Heading: <${notification.header}> | Message: <${
                            notification.text
                        }> - Error object: ${JSON.stringify(parsedError, null, 2)}`,
                        {
                            component: COMPONENTS.CART_PROVIDER,
                            severity: 'HIGH',
                            additionalInfo: CART_ERROR_MESSAGES.VALIDATE_CART_FAILED,
                        }
                    );
                } else {
                    logConsoleError(JSON.stringify(error), {
                        component: COMPONENTS.CART_PROVIDER,
                        severity: 'HIGH',
                        additionalInfo: `${CART_ERROR_MESSAGES.VALIDATE_CART_FAILED} | Error code: ${errorCode}`,
                    });
                }
            }
        } else {
            logConsoleError(JSON.stringify(error), {
                component: COMPONENTS.CART_PROVIDER,
                severity: 'HIGH',
                additionalInfo: CART_ERROR_MESSAGES.VALIDATE_CART_FAILED,
            });
        }
    };

    const handleCartError = useCallback(
        (error?: unknown, itemsOverride?: ItemsListItem[]): BaseError[] => {
            const parsedErrors = parseCartFlowErrorToValidationDetails(error);
            const itemsForParsing = itemsOverride ?? itemsList;
            const setCartErrorsAndStockErrors = (
                nextParsedErrors: CartValidationErrorDetails[],
                attemptedFulfillmentMethod?: DeliveryType
            ) => {
                setErrors([...nextParsedErrors]);

                if (nextParsedErrors.length) {
                    const parsedStockErrors = parseStockErrors(
                        nextParsedErrors,
                        itemsForParsing,
                        attemptedFulfillmentMethod
                    ) as StockErrorItem[];

                    setStockErrors(parsedStockErrors);
                }
            };

            if (error instanceof CheckoutPaymentError) {
                setErrors([...parsedErrors]);
            } else if (error instanceof CartUpdateError) {
                setCartErrorsAndStockErrors(parsedErrors, error?.attemptedFulfillmentMethod);
            } else if (error instanceof CartValidationError) {
                setCartErrorsAndStockErrors(parsedErrors);
            } else if (error instanceof Error) {
                setErrors([...parsedErrors]);
            }

            logParsedErrors(error, parsedErrors);

            return parsedErrors;
        },
        [itemsList]
    );

    const removeErrorByCode = useCallback((errorCode: string) => {
        setErrors(currentErrors => currentErrors.filter(error => error.errorCode !== errorCode));
    }, []);

    const clearErrors = useCallback(() => setErrors([]), []);
    const clearStockErrors = useCallback(() => setStockErrors([]), []);

    const updateNotifications = (updatedCart: CartUI | null) => {
        if (!updatedCart) {
            return;
        }
        // Always merge (including `[]` / missing) so client notification state matches cart snapshots after validate.
        setNotifications(prev =>
            mergeCartNotificationsPreservingDiscontinuedRemoved(prev, updatedCart.notifications ?? [])
        );
    };

    const refreshCartState = useCallback(
        async (options: RefetchCartStateOptions = {}): Promise<CartUI | null> => {
            const {
                preserveUpdatingState = false,
                validate = false,
                mergePersistedNotifications = false,
                cartId: cartIdOverride,
                previousCart: previousCartOverride,
                discardIfCartChanged = false,
                ignorePreserveUpdatingHold = false,
            } = options;

            // skipCommit has not published React state yet. A GET (sign-in merge recovery,
            // validation recovery) would leak the intermediate server cart and can bump version
            // so the follow-up date re-apply PUT is stale.
            if (isPreserveUpdatingHoldBlocking(ignorePreserveUpdatingHold)) {
                return latestCartRef.current;
            }

            const effectiveCartId = cartIdOverride ?? cartId;
            const previousCartForEnrichment = previousCartOverride ?? cart;
            const cartBeforeRefetch = latestCartRef.current;

            // record the attempt up front so a cart that fails to load is not retried on every focus,
            // keeping the previous id to roll back to if the attempt throws.
            const previouslySyncedCartId = lastSyncedCartIdRef.current;
            lastSyncedCartIdRef.current = effectiveCartId || undefined;

            if (!preserveUpdatingState) {
                beginUpdating();
            }

            try {
                const cartRes = effectiveCartId
                    ? await getCartById({
                          cartId: effectiveCartId,
                          ...(validate ? { validate: true } : {}),
                      })
                    : null;

                // another write replaced the cart while this fetch was in flight, so this response is
                // already the older snapshot. Hand back the newer cart instead of writing over it.
                if (
                    shouldAbandonUnpublishedRefetch(discardIfCartChanged, cartBeforeRefetch, ignorePreserveUpdatingHold)
                ) {
                    return restorePreviouslySyncedCartId(previouslySyncedCartId);
                }

                if (!cartRes?.id) {
                    return null;
                }

                // preserve enriched data if we have a previous cart with the same ID
                const updatedCart = await enrichCart(cartRes, previousCartForEnrichment);

                // enrich is async; a skipCommit hold can land before we publish.
                if (isPreserveUpdatingHoldBlocking(ignorePreserveUpdatingHold)) {
                    return restorePreviouslySyncedCartId(previouslySyncedCartId);
                }

                setCart(updatedCart);
                if (mergePersistedNotifications) {
                    setNotifications(prev =>
                        mergeValidationNotificationsForSingleUpdateCycle(
                            prev ?? null,
                            updatedCart.notifications ?? null
                        )
                    );
                } else {
                    updateNotifications(updatedCart);
                }

                return updatedCart;
            } catch (refreshError) {
                // a failed attempt is not a sync, so callers comparing the ref against the cookie can
                // detect the divergence again and retry.
                lastSyncedCartIdRef.current = previouslySyncedCartId;
                throw refreshError;
            } finally {
                if (!preserveUpdatingState) {
                    endUpdating();
                }
            }
        },
        [cartId, enrichCart, cart]
    );

    const refetchCart = useCallback(
        async (options?: RefetchCartStateOptions): Promise<CartUI | null> => {
            return refreshCartState(options);
        },
        [refreshCartState]
    );

    const processCartValidationFailure = async (
        error: unknown,
        updatedCart: CartUI,
        preservedErrors: CartValidationErrorDetails[]
    ): Promise<void> => {
        const itemsForParsing = prepareItemsForParsing(updatedCart);
        const parsedValidationErrors = handleCartError(error, itemsForParsing);

        setErrors(mergePreservedValidationErrors(preservedErrors, parsedValidationErrors));

        if (error instanceof CartValidationError) {
            const validationNotifications = getNotificationsFromCartValidationResponse(error.errorDetails);

            const notificationsToSet = filterScheduledDeliveryNotificationsForValidationErrors(
                parsedValidationErrors,
                validationNotifications
            );

            if (notificationsToSet.length > 0) {
                setNotifications(prev =>
                    mergeValidationNotificationsForSingleUpdateCycle(prev ?? null, notificationsToSet)
                );
            }

            await refetchAndPatchCartAfterScheduleClearedOnValidateFailure(
                parsedValidationErrors,
                validationNotifications,
                cartId,
                refreshCartState,
                setCart,
                applyCartDerivedLists
            );
        }

        logConsoleError(JSON.stringify(error), {
            component: COMPONENTS.CART_PROVIDER,
            severity: 'HIGH',
            additionalInfo: CART_ERROR_MESSAGES.VALIDATE_CART_FAILED,
        });
    };

    const handleCartValidation = async (
        updatedCart: CartUI,
        preservedErrors: CartValidationErrorDetails[] = [],
        options: CartHandleValidationOptions = {}
    ): Promise<CartUI> => {
        if (options.replayValidationFailure) {
            await processCartValidationFailure(options.replayValidationFailure, updatedCart, preservedErrors);

            return updatedCart;
        }

        try {
            // skip validation if cart is empty
            if (updatedCart.fulfillmentGroups?.length !== 0) {
                const response = await validateCart({
                    cartId: updatedCart.id,
                    useRealtimeInventory: false,
                });

                if (response && 'id' in response) {
                    const validatedCart = await enrichCart(
                        response,
                        updatedCart.id === response.id ? updatedCart : undefined
                    );
                    const validatedCartForCurrentUpdateCycle =
                        'mergeNotificationsFromUpdateCycle' in options
                            ? {
                                  ...validatedCart,
                                  notifications: mergeValidationNotificationsForSingleUpdateCycle(
                                      options.mergeNotificationsFromUpdateCycle,
                                      validatedCart.notifications
                                  ),
                              }
                            : validatedCart;

                    setCart(validatedCartForCurrentUpdateCycle);
                    updateNotifications(validatedCartForCurrentUpdateCycle);
                    setErrors([...preservedErrors]);

                    return validatedCartForCurrentUpdateCycle;
                }
            }

            setErrors([...preservedErrors]);
        } catch (error: unknown) {
            const parsedForMerge = parseCartFlowErrorToValidationDetails(error);
            const mergedErrors = mergePreservedValidationErrors(preservedErrors, parsedForMerge);

            const hasDiscontinuedError = mergedErrors.some(
                e => e.errorCode === CartValidationErrorKey.DISCONTINUED_PRODUCT
            );

            if (hasDiscontinuedError) {
                const remainingValidationErrors = excludeDiscontinuedValidationErrors(mergedErrors);
                let cartAfterDiscontinuedRecovery = updatedCart;

                logParsedErrors(error, parsedForMerge);
                await runDiscontinuedItemsRemovedRecovery({
                    validationErrors: mergedErrors,
                    setNotifications,
                    removeLineItemsFromCartState,
                    refetchCart: async () => {
                        const refetchedCart = await refreshCartState({
                            preserveUpdatingState: true,
                            mergePersistedNotifications: true,
                        });
                        if (refetchedCart) {
                            cartAfterDiscontinuedRecovery = refetchedCart;
                        }
                    },
                    clearStockErrors,
                    clearErrors,
                    whenRefetchFails: 'continue',
                    onRefetchFailure: refetchError => {
                        logConsoleError(JSON.stringify(refetchError), {
                            component: COMPONENTS.CART_PROVIDER,
                            severity: 'HIGH',
                            additionalInfo: CART_ERROR_MESSAGES.FETCH_CART_FAILED,
                        });
                    },
                });

                if (remainingValidationErrors.length > 0) {
                    setErrors(remainingValidationErrors);
                    setStockErrors(
                        parseStockErrors(
                            remainingValidationErrors,
                            prepareItemsForParsing(cartAfterDiscontinuedRecovery)
                        ) as StockErrorItem[]
                    );

                    if (error instanceof CartValidationError) {
                        const validationNotifications = getNotificationsFromCartValidationResponse(error.errorDetails);

                        const notificationsToSet = filterScheduledDeliveryNotificationsForValidationErrors(
                            remainingValidationErrors,
                            validationNotifications
                        );

                        if (notificationsToSet.length > 0) {
                            setNotifications(prev =>
                                mergeValidationNotificationsForSingleUpdateCycle(prev ?? null, notificationsToSet)
                            );
                        }

                        await refetchAndPatchCartAfterScheduleClearedOnValidateFailure(
                            remainingValidationErrors,
                            validationNotifications,
                            cartId,
                            refreshCartState,
                            setCart,
                            applyCartDerivedLists
                        );
                    }
                }

                // Signal the cart-app consumer to redirect to /cart; navigation is the consumer's
                // job so this shared hook never depends on the App Router. Only fires on
                // PageType.CHECKOUT, which is set exclusively in the cart app.
                if (pageType === PageType.CHECKOUT) {
                    setShouldRedirectToCart(true);
                }
                return cartAfterDiscontinuedRecovery;
            }

            await processCartValidationFailure(error, updatedCart, preservedErrors);
        }

        return updatedCart;
    };

    const throwOnCartUpdateFailure = (updatedCart: CartUI | null, body: unknown): void => {
        const isCartUpdateFailure =
            hasFulfillmentMethodCannotBeChangedError(updatedCart, body) ||
            (updatedCart?.status &&
                isErrorStatus(updatedCart.status) &&
                !(updatedCart as unknown as ErrorResponseBody)?.invalid_fields);

        if (!isCartUpdateFailure) {
            return;
        }

        throw new CartUpdateError(
            'Bad request',
            updatedCart as unknown as CartBadRequestErrorResponse,
            (body as UpdateItemFulfillmentMethodRequestBody | undefined)?.fulfillmentMethod
        );
    };

    const isLogoutError = (updatedCart: CartUI | null) => {
        if (!updatedCart || !('invalid_fields' in updatedCart)) {
            return false;
        }

        const invalidFields = updatedCart.invalid_fields;

        const fields: InvalidFieldError[] = Array.isArray(invalidFields)
            ? invalidFields.flat()
            : Object.values(invalidFields as Record<string, InvalidFieldError>);

        return fields.some(
            field =>
                field?.errorCode === INVALID_FIELD_ERROR.PREDICATE_VALIDATOR &&
                field?.message === INVALID_FIELD_ERROR.CUSTOMER_ID_OR_ANONYMOUS_ID_NOT_PROVIDED
        );
    };

    const shouldRefreshStoreData = (body: unknown): boolean => {
        const storeId = (body as UpdateItemStoreRequestBody | undefined)?.storeId;
        const fulfillmentMethod = (body as UpdateItemFulfillmentMethodRequestBody | undefined)?.fulfillmentMethod;

        return (storeId !== undefined && fulfillmentMethod === undefined) || fulfillmentMethod === DeliveryType.BOPIS;
    };

    const applyUpdatedCartState = async (updatedCart: CartUI, previousCart: CartUI, body: unknown): Promise<CartUI> => {
        const enrichedCart = await enrichCart(updatedCart, previousCart, {
            refreshStoreData: shouldRefreshStoreData(body),
        });
        const preservedFulfillmentMethodErrors = getPreservedFulfillmentMethodErrors(
            errors,
            enrichedCart,
            previousCart
        );

        setCart(enrichedCart);
        const validatedCart = await handleCartValidation(enrichedCart, preservedFulfillmentMethodErrors, {
            mergeNotificationsFromUpdateCycle: enrichedCart.notifications,
        });

        const isPromoApply = body != null && typeof body === 'object' && 'code' in body;
        const clientCodesToRetry = (previousCart.discountCodes ?? []).filter(
            discCode => isClientIneligibleDiscountCode(discCode) && discCode.code != null
        );

        if (!isPromoApply && clientCodesToRetry.length > 0) {
            let latestCart = validatedCart;
            for (const dc of clientCodesToRetry) {
                try {
                    const result = await addDiscountCodeApi({
                        cartId: latestCart.id,
                        version: latestCart.version,
                        body: { code: dc.code },
                    });
                    if (result?.id) {
                        latestCart = await enrichCart(result, latestCart);
                        setCart(latestCart);
                    }
                } catch {
                    // backend still rejects — keep client-patched entry
                }
            }

            return latestCart;
        }

        return validatedCart;
    };

    const reconcileExternalCartUpdate = async (updatedCart: CartUI): Promise<CartUI> => {
        beginUpdating();
        // The skipCommit caller is committing now. Drop the hold so validation-recovery
        // refetches during this commit can publish; isUpdating stays true via in-flight.
        clearPreserveUpdatingHold();

        try {
            if (!cart) {
                const enrichedCart = await enrichCart(updatedCart);

                setCart(enrichedCart);

                return await handleCartValidation(enrichedCart);
            }

            return await applyUpdatedCartState(updatedCart, cart, undefined);
        } finally {
            endUpdating();
        }
    };

    const shouldRefreshAfterFulfillmentMethodError = (
        error: unknown,
        parsedErrors: CartValidationErrorDetails[]
    ): boolean => {
        return (
            error instanceof CartUpdateError &&
            error.attemptedFulfillmentMethod === DeliveryType.STH &&
            getFulfillmentMethodErrors(parsedErrors).length > 0
        );
    };

    const refreshCartAfterFulfillmentMethodError = async (
        parsedErrors: CartValidationErrorDetails[],
        cartIdOverride?: string
    ): Promise<void> => {
        // A prior skipCommit PUT can leave this hold set so merge/date-reapply GETs cannot leak
        // an intermediate cart. The skipCommit sequence has failed; drop it so recovery can GET
        // the server cart. isUpdating stays true via the in-flight write, and that write's finally
        // re-arms the hold when preserveUpdatingState was requested.
        clearPreserveUpdatingHold();

        try {
            const refreshedCart = await refreshCartState({
                preserveUpdatingState: true,
                // Follow-up PUTs after merge target mergeResponse.id. The hook/cookie id can still
                // be the pre-merge cart, so recovery must GET the write's cart, not the stale one.
                ...(cartIdOverride ? { cartId: cartIdOverride } : {}),
            });

            if (refreshedCart?.id) {
                await handleCartValidation(refreshedCart, parsedErrors);
            }
        } catch (refreshError) {
            logConsoleError(JSON.stringify(refreshError), {
                component: COMPONENTS.CART_PROVIDER,
                severity: 'HIGH',
                additionalInfo: `${CART_ERROR_MESSAGES.CART_UPDATE_FAILED} | Cart refetch failed after fulfillment method update error`,
            });
        }
    };

    const handleValidationErrorOnCartUpdate = async (currentCart: CartUI): Promise<void> => {
        try {
            await validateCart({
                cartId: currentCart.id,
                useRealtimeInventory: false,
                silentUpdate: true,
                throwOnError: false,
            });
        } catch (validationError: unknown) {
            if (validationError instanceof CartValidationError) {
                const itemsForParsing = prepareItemsForParsing(currentCart);
                const preservedFulfillmentMethodErrors = getPreservedFulfillmentMethodErrors(errors, currentCart);
                const parsedValidationErrors = handleCartError(validationError, itemsForParsing);

                setErrors(mergePreservedValidationErrors(preservedFulfillmentMethodErrors, parsedValidationErrors));

                const validationNotifications = getNotificationsFromCartValidationResponse(
                    validationError.errorDetails
                );

                const notificationsToSetOnUpdate = filterScheduledDeliveryNotificationsForValidationErrors(
                    parsedValidationErrors,
                    validationNotifications
                );

                if (notificationsToSetOnUpdate.length > 0) {
                    setNotifications(prev =>
                        mergeValidationNotificationsForSingleUpdateCycle(prev ?? null, notificationsToSetOnUpdate)
                    );
                }
            }
        }
    };

    const handleUpdateCartStateError = async (
        error: unknown,
        currentCart: CartUI,
        options?: CartUpdateOptions,
        body?: unknown
    ): Promise<CartUI | null> => {
        logConsoleError(JSON.stringify(error), {
            component: COMPONENTS.CART_PROVIDER,
            severity: 'HIGH',
            additionalInfo: CART_ERROR_MESSAGES.CART_UPDATE_FAILED,
            extra: {
                cart: JSON.stringify({
                    cartId: currentCart.id,
                    payload: itemsList.map(item => ({
                        id: item.id,
                        scheduledDeliveryDate: item.scheduledDeliveryDate ?? null,
                    })),
                }),
            },
        });

        if (getShouldIgnoreError(error)) {
            const promoCode = (body as ApplyPromoCodeRequestBody | undefined)?.code;
            if (promoCode) {
                const codeUpper = promoCode.toUpperCase();
                const alreadyOnCart = (currentCart.discountCodes ?? []).some(
                    dc => dc.code?.toUpperCase() === codeUpper
                );
                const errorMessage =
                    error instanceof CartUpdateError ? (error.errorDetails?.errors?.[0]?.message ?? '') : '';
                const isAutoshipIncompatible = errorMessage === 'Promo code is invalid or expired';
                if (!alreadyOnCart && isAutoshipIncompatible) {
                    const clientIneligibleDiscountCode = createClientIneligibleDiscountCode(codeUpper);
                    const patchedCart: CartUI = {
                        ...currentCart,
                        discountCodes: [...(currentCart.discountCodes ?? []), clientIneligibleDiscountCode],
                    };
                    setClientIneligibleCodesInStorage(currentCart.id, [
                        ...getClientIneligibleCodesFromStorage(currentCart.id),
                        clientIneligibleDiscountCode.code,
                    ]);
                    setCart(patchedCart);
                    return patchedCart;
                }
                // Not autoship-incompatible — return error so form shows "invalid"
                return error as CartUI;
            }
            return error as CartUI;
        }

        if (getShouldIgnorePaymentError(error)) {
            return null;
        }

        const parsedErrors = handleCartError(error);

        if (shouldRefreshAfterFulfillmentMethodError(error, parsedErrors)) {
            await refreshCartAfterFulfillmentMethodError(getFulfillmentMethodErrors(parsedErrors), options?.cartId);
            return null;
        }

        if (options?.validateCartOnError) {
            await handleValidationErrorOnCartUpdate(currentCart);
        }

        return null;
    };

    const updateCartState = async <T>(
        cartUpdater: CartUpdater<T>,
        body: T | null = null,
        itemId = '',
        options?: CartUpdateOptions
    ): Promise<CartUI | null> => {
        if (!cart) {
            return null;
        }

        const previousCart = cart;

        // Client-ineligible codes only exist in client state — remove them without an API call.
        const clientIneligibleId =
            body != null &&
            typeof body === 'object' &&
            'codeId' in body &&
            typeof (body as { codeId: unknown }).codeId === 'string' &&
            isClientIneligibleDiscountCodeId((body as { codeId: string }).codeId)
                ? (body as { codeId: string }).codeId
                : null;

        if (clientIneligibleId) {
            const codeUpper = getCodeFromClientIneligibleDiscountCodeId(clientIneligibleId);
            setClientIneligibleCodesInStorage(
                previousCart.id,
                getClientIneligibleCodesFromStorage(previousCart.id).filter(code => code !== codeUpper)
            );
            const patchedCart: CartUI = {
                ...previousCart,
                discountCodes: (previousCart.discountCodes ?? []).filter(dc => dc.id !== clientIneligibleId),
            };
            setCart(patchedCart);
            return patchedCart;
        }

        try {
            beginUpdating();

            let updatedCart = await cartUpdater({
                body: body as T,
                cartId: (options?.cartId ?? previousCart.id).toString(),
                itemId,
                version: options?.version ?? previousCart.version,
            });

            throwOnCartUpdateFailure(updatedCart, body);

            if (isLogoutError(updatedCart)) {
                openAccountDrawer(AccountDrawerType.SIGN_IN);
                return null;
            }

            if (!updatedCart?.id) {
                return updatedCart;
            }

            if (options?.skipCommit) {
                return updatedCart;
            }

            updatedCart = await applyUpdatedCartState(updatedCart, previousCart, body);

            return updatedCart;
        } catch (error: unknown) {
            return await handleUpdateCartStateError(error, previousCart, options, body);
        } finally {
            if (options?.preserveUpdatingState) {
                hasPreserveUpdatingHoldRef.current = true;
            }
            endUpdating();
        }
    };

    const dropHeldCart = () => {
        setCart(EMPTY_CART);
        applyCartDerivedLists(EMPTY_CART);
    };

    const clearCart = () => {
        setClientIneligibleCodesInStorage(cart?.id ?? cartId, []);
        dropHeldCart();
        lastSyncedCartIdRef.current = undefined;
        Cookies.remove(COOKIES.CART_ID);
        Cookies.remove(COOKIES.IS_SHIPPING_OPTION_CONFIRMED);
        Cookies.remove(COOKIES.EMAIL_OPTIONAL_IN);
    };

    const refreshCartWithSubscriptionStatusOnInit = async (
        cart: CartUI
    ): Promise<{ cart: CartUI; prefetchedProductMap?: Map<number | undefined, ProductHit> }> => {
        let nextCart = cart;
        const cartBeforeSubscriptionRefresh = nextCart;
        const [subscriptionResult, parallelProductMap] = await Promise.all([
            updateSubscriptionStatus({
                cartId: nextCart.id,
                version: nextCart.version,
            }),
            getCartProductsByIds(nextCart),
        ]);

        if (subscriptionResult?.id) {
            // The subscription-status refresh returns a plain cart snapshot without validate-on-load
            // banners (discontinued removed, max qty, etc.). Preserve those so they still reach
            // notifications state instead of being overwritten here.
            nextCart = {
                ...subscriptionResult,
                notifications: mergeCartNotificationsPreservingValidateOnLoad(
                    cart.notifications,
                    subscriptionResult.notifications
                ),
            };
        }

        const prefetchedProductMap = areCartSkuSetsEqual(cartBeforeSubscriptionRefresh, nextCart)
            ? parallelProductMap
            : await getCartProductsByIds(nextCart);

        return { cart: nextCart, prefetchedProductMap };
    };

    useEffect(() => {
        const isConfirmationPage = pathname === NAVIGATION_URLS.velocity.orderConfirmation;

        const initializeCart = async () => {
            if (init.current || isConfirmationPage) {
                return;
            }

            try {
                init.current = true;

                // Validate-on-load only on the cart page, where discontinued items would otherwise
                // paint and then flicker out. Other consumers (mini-cart, etc.) keep the plain GET.
                let cartRes = cartId ? await getCartById({ cartId, validate: pageType === PageType.CART }) : null;

                let prefetchedProductMap: Map<number | undefined, ProductHit> | undefined;

                const shouldRefreshSubscriptionStatusOnInit =
                    Boolean(cartRes?.id) &&
                    Boolean(cartRes?.version) &&
                    pageType === PageType.CART &&
                    Boolean(isLoggedIn) &&
                    autoshipDiscountUpdatesEnabled;

                if (shouldRefreshSubscriptionStatusOnInit && cartRes) {
                    const subscriptionRefresh = await refreshCartWithSubscriptionStatusOnInit(cartRes);
                    cartRes = subscriptionRefresh.cart;
                    prefetchedProductMap = subscriptionRefresh.prefetchedProductMap;
                }

                if (cartRes?.status === HttpStatus.NOT_FOUND) {
                    setIsCartNotFound(true);

                    logConsoleError(JSON.stringify(cartRes), {
                        component: COMPONENTS.CART_PROVIDER,
                        severity: 'HIGH',
                        additionalInfo: CART_ERROR_MESSAGES.CART_NOT_FOUND,
                    });
                    handleCartError(cartRes);

                    return;
                }

                if (cartRes?.id) {
                    let updatedCart = await enrichCart(
                        cartRes,
                        undefined,
                        prefetchedProductMap ? { prefetchedProductMap } : undefined
                    );

                    if (pageType === PageType.CART && consumePendingScheduledDeliveryDateChange(updatedCart)) {
                        updatedCart = {
                            ...updatedCart,
                            notifications: replaceWithLineItemAgnosticScheduledDeliveryDateUpdatedNotification(
                                updatedCart.notifications
                            ),
                        };
                        logSentryError(new Error('Order placement with scheduled delivery date failed'), {
                            sentryContext: SENTRY_CONTEXT.ORDER_ERROR,
                            tags: { component: COMPONENTS.ORDER_SUMMARY_CHECKOUT, severity: 'HIGH' },
                        });
                    }

                    setCart(updatedCart);
                    setNotifications(prev =>
                        mergeCartNotificationsPreservingDiscontinuedRemoved(prev, updatedCart.notifications ?? [])
                    );
                }
            } catch (error) {
                handleCartError(error);
                logConsoleError(JSON.stringify(error), {
                    component: COMPONENTS.CART_PROVIDER,
                    severity: 'HIGH',
                    additionalInfo: CART_ERROR_MESSAGES.CART_INITIALIZATION_FAILED,
                });
            } finally {
                setIsLoading(false);
            }
        };

        initializeCart();
    }, [cartId, enrichCart, handleCartError]);

    // any successful cart write (init, add-to-cart, updates) becomes the identity this tab owns.
    useEffect(() => {
        if (cart?.id) {
            lastSyncedCartIdRef.current = cart.id;
        }
    }, [cart?.id]);

    useEffect(() => {
        latestCartRef.current = cart;
    }, [cart]);

    // latest-ref pattern: this closes over `clearCart` and `refreshCartState`, which are recreated on
    // most renders, so holding it in a ref lets the listeners below subscribe once.
    const reconcileCartIdentity = useRef<() => Promise<void>>(async () => {});

    useEffect(() => {
        reconcileCartIdentity.current = async () => {
            // a tab made visible mid-load has nothing to compare against yet, so record the pass for
            // the effect below to retry once init settles. `isLoading` never turns false on the
            // order-confirmation page, so nothing runs there, it has just cleared the cart anyway.
            if (isLoading) {
                hasPendingReconcileRef.current = true;
                return;
            }

            // a cart write in flight resolves with its own setCart, which would put back the cart this
            // is about to drop. Defer to that write and reconcile once it settles.
            if (isUpdating) {
                hasPendingReconcileRef.current = true;
                return;
            }

            hasPendingReconcileRef.current = false;

            const cookieCartId = Cookies.get(COOKIES.CART_ID);

            if (!isCartIdentityStale(cookieCartId, lastSyncedCartIdRef.current)) {
                return;
            }

            if (!cookieCartId) {
                // checked out or signed out in another tab, so fall back to an empty guest cart.
                clearCart();
                return;
            }

            // during checkout, keep the cart being paid for rather than swapping it underneath the
            // shopper.
            if (pageType === PageType.CHECKOUT) {
                return;
            }

            try {
                // the shopper did not ask for this refetch, so it must lose to any write that lands
                // while it is in flight rather than reverting that write's result.
                const refreshedCart = await refreshCartState({
                    cartId: cookieCartId,
                    discardIfCartChanged: true,
                });

                if (!refreshedCart) {
                    // The cookie names a cart this tab cannot load, so drop the one it is holding rather
                    // than rendering a cart it no longer owns.
                    dropHeldCart();
                }
            } catch (refreshError) {
                logConsoleError(JSON.stringify(refreshError), {
                    component: COMPONENTS.CART_PROVIDER,
                    severity: 'HIGH',
                    additionalInfo: `${CART_ERROR_MESSAGES.CART_UPDATE_FAILED} | Cart refetch failed while reconciling cart identity across tabs`,
                });
            }
        };
    });

    useEffect(() => {
        const reconcileWhenVisible = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            void reconcileCartIdentity.current();
        };

        // `focus` also covers side-by-side windows, where switching never changes visibility.
        document.addEventListener('visibilitychange', reconcileWhenVisible);
        window.addEventListener('focus', reconcileWhenVisible);

        return () => {
            document.removeEventListener('visibilitychange', reconcileWhenVisible);
            window.removeEventListener('focus', reconcileWhenVisible);
        };
    }, []);

    // run a reconcile that was deferred by the initial load or an in-flight write, so the tab does not
    // sit on a cart it no longer owns until the next focus.
    useEffect(() => {
        if (isLoading || isUpdating || !hasPendingReconcileRef.current) {
            return;
        }

        void reconcileCartIdentity.current();
    }, [isLoading, isUpdating]);

    return {
        cart,
        idsList,
        itemsList,
        isLoading,
        isUpdating,
        enrichCart,
        setCart,
        dropHeldCart,
        clearCart,
        setUpdating,
        updateCartState,
        errors,
        handleCartError,
        cartShippingOptionsMessage,
        setCartShippingOptionsMessage,
        removeErrorByCode,
        clearErrors,
        isCartNotFound,
        handleCartValidation,
        stockErrors,
        clearStockErrors,
        refetchCart,
        reconcileExternalCartUpdate,
        removeLineItemsFromCartState,
        shippingMethodsList,
        setShippingMethodsList,
        shouldRedirectToCart,
    };
};

export default useCartState;
